import { memo, useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { IGeneralResultDecision } from 'KnmErknmPackage/ErkmAgreementsModule/interfaces/IGeneralResultDecision';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import styles from './AgreementsEliminateViolations.module.scss';
import { AgreementsEliminateViolationsAttachment } from './AgreementsEliminateViolationsAttachment';

interface IAgreementsEliminateViolationsProps {
  id: string;
  isWatchOnly: boolean;
  name: string;
  agreement: IErknmFormAgreement;
  resultDecisions: IGeneralResultDecision[] | undefined;
}

interface IAgreementsEliminateViolationsStoreProps {
  formRef: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

export const AgreementsEliminateViolationsWithoutStore = memo(
  ({
    id,
    isWatchOnly,
    name,
    agreement,
    formRef,
    decisionFormRef,
    resultDecisions,
  }: IAgreementsEliminateViolationsProps & IAgreementsEliminateViolationsStoreProps) => {
    const { isKnm, isPm, isDecision } = useContext(AgreementsContext);

    const minConclusionDate = useMemo(() => {
      if (resultDecisions) {
        const resultDecision = resultDecisions.find(decision => decision.guid === agreement.prescription?.value);

        if (resultDecision) {
          return parseDate({ date: resultDecision.date || '', parseFormat: DATE_FORMAT_LONG_MINUTE_FNS });
        }

        return new Date();
      }
    }, [agreement, resultDecisions]);

    return (
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Соглашение об устранении нарушений',
          field: `${name}.eliminateViolationsNumber`,
          isKnm,
          isPm,
          isDecision,
        })}
        id={`${id}.eliminateViolationsNumber`}
        className={styles.Wrapper}
      >
        <TextInputField
          name={`${name}.eliminateViolationsNumber`}
          placeholder={'Номер соглашения'}
          size="medium"
          disabled={isWatchOnly}
        />
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: `Дата заключения соглашения`,
            field: `${name}.conclusionDate`,
            isKnm,
            isPm,
            isDecision,
          })}
          id={`${id}.conclusionDate`}
          placementLabel="left"
          color="black"
          className={cn(styles.FieldBlock, styles.MarginTop)}
        >
          <DatePickerField
            name={`${name}.conclusionDate`}
            placeholder={'Дата заключения'}
            size="medium"
            disabled={isWatchOnly}
            minDate={minConclusionDate}
          />
        </FieldBlock>
        <AgreementsEliminateViolationsAttachment
          isWatchOnly={isWatchOnly}
          name={`${name}.eliminateViolationsAttachment`}
          attachment={agreement?.eliminateViolationsAttachment}
          formRef={formRef || decisionFormRef}
          id={`${id}.eliminateViolationsAttachment`}
        />
      </FieldBlock>
    );
  },
);

const mapStateToProps = (state: IRootState): IAgreementsEliminateViolationsStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const AgreementsEliminateViolations = connect(mapStateToProps)(AgreementsEliminateViolationsWithoutStore);
